import { FieldNames } from '@/constants/FieldNames';
import IComp, { newComp } from '@/models/Comp';
import ICompAddress, { newCompAddress } from '@/models/CompAddress';
import IFeedData from '@/models/comp/IFeedData';
import IMlsPropertyDataV1 from '@/models/mls/IMlsPropertyDataV1';
import IMLSPropertyV1 from '@/models/mls/IMLSPropertyV1';
import IMLSData from '@/models/mls/IMLSData';
import IMLSPropertyItem, { newMlsPropertyItem } from '@/models/mls/IMLSPropertyItem';
import IMLSPropertyData from '@/models/mls/IMLSPropertyData';
import utils from '@/utils/QSUtils';

export default {
    /**
     * Convert simple MLS data to the complex type (new structure).
     * @param propertyDataV1
     * @returns
     */
    convertToV2(propertyDataV1: IMlsPropertyDataV1): IMLSPropertyData {
        const propertyData: IMLSPropertyData = [];

        for (const property of propertyDataV1) {
            const propertyItem: IMLSPropertyItem = newMlsPropertyItem();

            for (const key in property) {
                const mlsData: IMLSData = {
                    formData: property[key],
                    formFieldName: key,
                    friendlyName: undefined,
                    mappingName: undefined,
                    parentFormFieldName: undefined,
                    propertySource: [],
                    relatedFields: [],
                };
                propertyItem.fields.push(mlsData);
            }
            propertyData.push(propertyItem);
        }
        return propertyData;
    },

    /**
     * Convert complex MLS data to the simple type (old structure).
     * @param propertyData
     * @returns
     */
    convertToV1(propertyData: IMLSPropertyData): IMlsPropertyDataV1 {
        const propertyDataV1: IMlsPropertyDataV1 = [];

        for (const propertyItem of propertyData) {
            const propertyV1 = this.convertSingleToV1(propertyItem);
            propertyDataV1.push(propertyV1);
        }
        return propertyDataV1;
    },

    /**
     * Convert a single complex MLS property to the simple type (old structure)
     * @param propertyItem
     * @returns
     */
    convertSingleToV1(propertyItem: IMLSPropertyItem): IMLSPropertyV1 {
        const propertyV1: IMLSPropertyV1 = {};
        for (const field of propertyItem.fields) {
            propertyV1[field.formFieldName] = field.formData;
        }
        return propertyV1;
    },

    convertForCompsUpdate(propertyData: IMLSPropertyData): IComp[] {
        const comps: IComp[] = [];
        const propertyDataV1 = this.convertToV1(propertyData);
        for (const propertyV1 of propertyDataV1) {
            const address: ICompAddress = buildCompAddress(propertyV1);

            const feedData: IFeedData = {};
            feedData.sourceData = {};
            for (const key of Object.keys(propertyV1)) {
                feedData.sourceData[key] = propertyV1[key];
            }

            const comp: IComp = newComp();
            comp.address = address;
            comp.sourceDataMLS = feedData;

            comps.push(comp);
        }
        return comps;
    },
};

function buildCompAddress(propertyV1: IMLSPropertyV1): ICompAddress {
    const address: ICompAddress = newCompAddress();
    address.address1 = getFieldValueAsString(propertyV1, FieldNames.Address1);
    address.address2 = '';
    address.unitNumber = getFieldValueAsString(propertyV1, FieldNames.UnitNo);
    address.city = getFieldValueAsString(propertyV1, FieldNames.City);
    address.state = getFieldValueAsString(propertyV1, FieldNames.State);
    address.zipCode = getFieldValueAsString(propertyV1, FieldNames.ZipCode);
    return address;
}

function getFieldValueAsString(propertyV1: IMLSPropertyV1, fieldName: FieldNames, defaultValue = ''): string {
    if (utils.isKeyInObject(propertyV1, fieldName)) {
        const value = propertyV1[fieldName];
        if (typeof value === 'string') {
            return value;
        }
        return '' + value;
    }
    return defaultValue;
}
